<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="comments"/> AI Agent - Lịch sử hội thoại</h1>
    </div>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-2">
        <b-form-group label="Trạng thái">
          <b-form-select v-model="filter.hasError" :options="errorOptions" @change="load"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Tool lỗi">
          <b-form-select v-model="filter.hasToolError" :options="toolErrorOptions" @change="load"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Từ khóa">
          <b-form-input v-model="filter.keyword" placeholder="Tìm nội dung / lỗi..." @keyup.enter="load"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filter.from"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filter.to"/>
        </b-form-group>
      </div>
      <div class="col-md-2 d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3 mr-2">
          <font-awesome-icon icon="search"/> Lọc
        </b-button>
        <b-button variant="success" @click="exportCSV" class="mb-3" :disabled="exporting">
          <font-awesome-icon icon="download"/> CSV
        </b-button>
      </div>
    </div>

    <!-- Table -->
    <b-table :items="items" :fields="fields" striped hover responsive small show-empty empty-text="Không có dữ liệu">
      <template #cell(createTime)="data">
        {{ data.value | moment('DD/MM HH:mm') }}
      </template>
      <template #cell(client)="data">
        <span v-if="data.value">{{ data.value.name }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(bot)="data">
        <span v-if="data.value">{{ data.value.name }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(platform)="data">
        <b-badge :variant="platformVariant(data.item)">{{ platformLabel(data.item) }}</b-badge>
      </template>
      <template #cell(customer)="data">
        <span v-if="data.value">{{ data.value.name || data.value.phone || '—' }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(question)="data">
        <span :title="data.item.message ? data.item.message.content : ''">{{ truncate(data.item.message ? data.item.message.content : '', 60) }}</span>
      </template>
      <template #cell(content)="data">
        <span :title="data.value">{{ truncate(data.value, 60) }}</span>
      </template>
      <template #cell(status)="data">
        <div>
          <b-badge v-if="data.item.error" variant="danger">Lỗi</b-badge>
          <b-badge v-else-if="data.item.sendTime > 0" variant="success">Đã gửi</b-badge>
          <b-badge v-else variant="warning">Chờ</b-badge>
          <b-badge v-if="data.item.errors && data.item.errors.length" variant="warning" class="ml-1" :title="data.item.errors.map(e => '[' + e.tool + '] ' + e.message).join('\n')">{{ data.item.errors.length }} tool</b-badge>
        </div>
        <small v-if="data.item.errorMsg" class="text-danger d-block" style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" :title="data.item.errorMsg">{{ data.item.errorMsg }}</small>
      </template>
      <template #cell(timing)="data">
        {{ data.item.timing && data.item.timing.total ? (data.item.timing.total / 1000).toFixed(1) + 's' : '—' }}
      </template>
      <template #cell(tokens)="data">
        {{ data.item.tokenUsage ? data.item.tokenUsage.totalTokens : '—' }}
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" variant="outline-primary" @click="showDetail(data.item)">
          <font-awesome-icon icon="eye"/>
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center" v-if="totalDocs > 0">
      <small class="text-muted">{{ totalDocs }} kết quả</small>
      <b-pagination v-model="page" :total-rows="totalDocs" :per-page="pageSize" @change="onPageChange"/>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" title="Chi tiết hội thoại" size="xl" ok-only ok-title="Đóng" scrollable>
      <div v-if="detail">
        <!-- Info header -->
        <div class="row mb-2">
          <div class="col-md-3">
            <small class="text-muted">Khách hàng</small>
            <div>{{ detail.client ? detail.client.name : '—' }} <small class="text-muted" v-if="detail.client">({{ detail.client.domain }})</small></div>
          </div>
          <div class="col-md-3">
            <small class="text-muted">Bot</small>
            <div>{{ detail.bot ? detail.bot.name : '—' }}</div>
          </div>
          <div class="col-md-3">
            <small class="text-muted">Khách</small>
            <div>{{ detail.customer ? (detail.customer.name || detail.customer.phone || '—') : '—' }}</div>
          </div>
          <div class="col-md-3">
            <small class="text-muted">Nền tảng</small>
            <div><b-badge :variant="platformVariant(detail)">{{ platformLabel(detail) }}</b-badge></div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-3">
            <small class="text-muted">Thời gian</small>
            <div>{{ detail.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="col-md-3">
            <small class="text-muted">Xử lý</small>
            <div>
              <span v-if="detail.timing && detail.timing.total">{{ (detail.timing.total / 1000).toFixed(1) }}s <small class="text-muted" v-if="detail.timing.setup">(setup {{ detail.timing.setup }}ms)</small></span>
              <span v-else-if="!detail.sendTime && !detail.error" class="text-warning">Đang xử lý... ({{ Math.round((Date.now() - detail.createTime) / 1000) }}s)</span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="col-md-3">
            <small class="text-muted">Trạng thái</small>
            <div>
              <b-badge v-if="detail.error" variant="danger">Lỗi</b-badge>
              <b-badge v-else-if="detail.sendTime > 0" variant="success">Đã gửi</b-badge>
              <b-badge v-else variant="warning">Đang chờ</b-badge>
            </div>
          </div>
          <div class="col-md-3" v-if="detail.tokenUsage && detail.tokenUsage.totalTokens">
            <small class="text-muted">Token</small>
            <div>{{ detail.tokenUsage.totalTokens }} <small class="text-muted">(in={{ detail.tokenUsage.promptTokens }}, out={{ detail.tokenUsage.completionTokens }})</small></div>
          </div>
        </div>

        <!-- Error banner -->
        <div class="alert alert-danger py-2 mb-3" v-if="detail.errorMsg">
          <strong>Lỗi:</strong> {{ detail.errorMsg }}
        </div>

        <!-- Tool errors -->
        <div class="alert alert-warning py-2 mb-3" v-if="detail.errors && detail.errors.length">
          <strong>Tool errors ({{ detail.errors.length }}):</strong>
          <div v-for="(err, i) in detail.errors" :key="'err'+i" class="mt-1">
            <b-badge variant="warning">{{ err.tool }}</b-badge> {{ err.message }}
          </div>
        </div>

        <hr class="my-2"/>

        <!-- Conversation flow -->
        <div class="mb-3">
          <strong>Khách hỏi:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.message ? detail.message.content : '—' }}<b-badge v-if="detail.message && detail.message.dataType && detail.message.dataType !== 'text'" variant="info" class="ml-1">{{ detail.message.dataType }}</b-badge></div>
        </div>

        <div class="mb-3" v-if="detail.content">
          <strong>Bot trả lời:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.content }}</div>
        </div>

        <!-- Steps chi tiết -->
        <div class="mb-3" v-if="detail.steps && detail.steps.length">
          <strong>Quá trình xử lý ({{ detail.steps.length }} steps):</strong>
          <div class="mt-2">
            <div v-for="(step, i) in detail.steps" :key="'s'+i" class="mb-2">
              <!-- HumanMessage -->
              <div v-if="step.type === 'HumanMessage'" class="d-flex">
                <b-badge variant="secondary" class="mr-2 align-self-start" style="min-width: 36px">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <div><b-badge variant="light" class="border">HumanMessage</b-badge></div>
                  <div class="text-muted mt-1 step-content" :class="{'step-collapsed': !expandedSteps[i]}" @click="$set(expandedSteps, i, !expandedSteps[i])">{{ typeof step.content === 'string' ? step.content : JSON.stringify(step.content, null, 2) }}</div>
                </div>
              </div>

              <!-- AIMessage -->
              <div v-else-if="step.type === 'AIMessage'" class="d-flex">
                <b-badge variant="success" class="mr-2 align-self-start text-white" style="min-width: 36px">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <div>
                    <b-badge variant="success">AIMessage</b-badge>
                    <b-badge v-for="tc in (step.tool_calls || [])" :key="tc.name + tc.toolIndex" variant="info" class="ml-1">{{ tc.name }}</b-badge>
                  </div>
                  <!-- AI content -->
                  <div v-if="step.content" class="mt-1 step-content" :class="{'step-collapsed': !expandedSteps[i]}" @click="$set(expandedSteps, i, !expandedSteps[i])" style="white-space: pre-wrap;">{{ step.content }}</div>
                  <!-- Tool calls args -->
                  <div v-if="step.tool_calls && step.tool_calls.length" class="mt-1">
                    <div v-for="tc in step.tool_calls" :key="tc.name + tc.toolIndex" class="ml-3 mb-1">
                      <small class="text-info cursor-pointer" @click="$set(expandedSteps, 'tc_'+i+'_'+tc.toolIndex, !expandedSteps['tc_'+i+'_'+tc.toolIndex])">
                        <font-awesome-icon :icon="expandedSteps['tc_'+i+'_'+tc.toolIndex] ? 'chevron-down' : 'chevron-right'" class="mr-1"/>
                        <strong>{{ tc.name }}</strong>({{ tc.args ? Object.keys(tc.args).join(', ') : '' }})
                      </small>
                      <pre v-if="expandedSteps['tc_'+i+'_'+tc.toolIndex]" class="bg-dark text-light p-2 rounded mt-1 mb-0" style="font-size: 12px; max-height: 200px; overflow: auto;">{{ JSON.stringify(tc.args, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ToolMessage -->
              <div v-else-if="step.type === 'ToolMessage'" class="d-flex">
                <b-badge :variant="isToolError(step) ? 'warning' : 'info'" class="mr-2 align-self-start text-white" style="min-width: 36px">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <div>
                    <b-badge :variant="isToolError(step) ? 'warning' : 'info'">ToolMessage</b-badge>
                    <small class="text-muted ml-1" v-if="step.tool_call_id">{{ step.tool_call_id }}</small>
                  </div>
                  <div class="mt-1 step-content cursor-pointer" :class="{'step-collapsed': !expandedSteps[i]}" @click="$set(expandedSteps, i, !expandedSteps[i])">
                    <pre v-if="isJsonContent(step.content)" class="mb-0 p-2 rounded" :class="isToolError(step) ? 'bg-warning-light' : 'bg-light'" style="font-size: 12px; max-height: 300px; overflow: auto; white-space: pre-wrap;">{{ formatJson(step.content) }}</pre>
                    <div v-else class="text-muted" style="white-space: pre-wrap;">{{ step.content }}</div>
                  </div>
                </div>
              </div>

              <!-- Other -->
              <div v-else class="d-flex">
                <b-badge variant="secondary" class="mr-2 align-self-start" style="min-width: 36px">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <b-badge variant="secondary">{{ step.type }}</b-badge>
                  <div class="text-muted mt-1 step-content" :class="{'step-collapsed': !expandedSteps[i]}" @click="$set(expandedSteps, i, !expandedSteps[i])">{{ typeof step.content === 'string' ? step.content : JSON.stringify(step.content, null, 2) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending: no steps yet -->
        <div v-else-if="!detail.sendTime && !detail.error" class="text-center text-muted py-4">
          <font-awesome-icon icon="spinner" spin class="mr-1"/> AI đang xử lý, chưa có steps...
          <div class="mt-2"><b-button size="sm" variant="outline-primary" @click="refreshDetail">Tải lại</b-button></div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      items: [],
      page: 1,
      pageSize: 20,
      totalDocs: 0,
      filter: {
        hasError: '',
        hasToolError: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      },
      errorOptions: [
        { value: '', text: 'Tất cả' },
        { value: 'pending', text: 'Đang chờ' },
        { value: 'true', text: 'Có lỗi' },
        { value: 'false', text: 'Không lỗi' },
      ],
      toolErrorOptions: [
        { value: '', text: 'Tất cả' },
        { value: 'true', text: 'Có tool lỗi' },
      ],
      fields: [
        { key: 'createTime', label: 'Thời gian', class: 'text-nowrap' },
        { key: 'client', label: 'KH' },
        { key: 'bot', label: 'Bot' },
        { key: 'platform', label: 'Nền tảng', class: 'text-center' },
        { key: 'customer', label: 'Khách' },
        { key: 'question', label: 'Câu hỏi' },
        { key: 'content', label: 'Trả lời' },
        { key: 'status', label: 'TT', class: 'text-center' },
        { key: 'timing', label: 'TG', class: 'text-right' },
        { key: 'tokens', label: 'Token', class: 'text-right' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      showModal: false,
      detail: null,
      expandedSteps: {},
      exporting: false,
    };
  },
  methods: {
    load() {
      var params = {
        page: this.page,
        pageSize: this.pageSize,
      };
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.hasToolError) params.hasToolError = this.filter.hasToolError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/logs', { params: params })
        .then(res => {
          this.items = res.data.docs;
          this.totalDocs = res.data.totalDocs;
        });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    showDetail(item) {
      this.expandedSteps = {};
      this.$service.get('/ai-agent/logs/' + item._id)
        .then(res => {
          this.detail = res.data;
          this.showModal = true;
        });
    },
    refreshDetail() {
      if (!this.detail) return;
      this.$service.get('/ai-agent/logs/' + this.detail._id)
        .then(res => {
          this.detail = res.data;
        });
    },
    isToolError(step) {
      if (!step.content) return false;
      try {
        var result = JSON.parse(step.content);
        return result && result.success === false;
      } catch (e) {
        return false;
      }
    },
    isJsonContent(content) {
      if (!content) return false;
      try { JSON.parse(content); return true; } catch (e) { return false; }
    },
    formatJson(content) {
      try { return JSON.stringify(JSON.parse(content), null, 2); } catch (e) { return content; }
    },
    exportCSV() {
      this.exporting = true;
      var params = {};
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/export', { params: params, responseType: 'blob' })
        .then(function (res) {
          var url = window.URL.createObjectURL(new Blob([res.data]));
          var a = document.createElement('a');
          a.href = url;
          a.download = 'ai-agent-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .finally(function () {
          this.exporting = false;
        }.bind(this));
    },
    truncate(str, len) {
      if (!str) return '—';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    platformLabel(item) {
      if (item.account && item.account.type) {
        var labels = { facebook: 'Facebook', instagram: 'Instagram', zalo: 'Zalo', telegram: 'Telegram', whatsapp: 'WhatsApp' };
        return labels[item.account.type] || item.account.type;
      }
      if (item.livechat) return 'LiveChat';
      return '—';
    },
    platformVariant(item) {
      if (!item.account && item.livechat) return 'info';
      if (!item.account) return 'secondary';
      var variants = { facebook: 'primary', instagram: 'danger', zalo: 'info', telegram: 'info', whatsapp: 'success' };
      return variants[item.account.type] || 'secondary';
    },
    stepVariant(type) {
      if (type === 'HumanMessage') return 'light';
      if (type === 'AIMessage') return 'success';
      if (type === 'ToolMessage') return 'info';
      return 'secondary';
    },
  },
  mounted() {
    this.load();
  },
};
</script>
<style scoped>
.step-content {
  cursor: pointer;
}
.step-collapsed {
  max-height: 60px;
  overflow: hidden;
  position: relative;
}
.step-collapsed::after {
  content: '... (click để xem thêm)';
  position: absolute;
  bottom: 0;
  right: 0;
  background: linear-gradient(to right, transparent, white 40%);
  padding-left: 40px;
  font-size: 11px;
  color: #007bff;
}
.bg-warning-light {
  background-color: #fff3cd;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
